import { useEffect, useRef } from "react";
import { useInView } from "framer-motion";
import { Helmet } from "react-helmet-async";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PricingHero from "@/components/pricing/PricingHero";
import PricingTier from "@/components/pricing/PricingTier";
import EnterpriseTier from "@/components/pricing/EnterpriseTier";
import PricingFAQ from "@/components/pricing/PricingFAQ";
import PricingCTA from "@/components/pricing/PricingCTA";
import { trackEvent } from "@/utils/tagManagerUtils";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles } from "lucide-react";
import TrialButton from "@/components/mcp/TrialButton";

const Pricing = () => {
  const pricingRef = useRef<HTMLElement>(null);
  const isInView = useInView(pricingRef, { once: true, amount: 0.2 });
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, []);
  
  const tiers = [
    {
      name: "Solo",
      monthlyPrice: 99,
      annualPrice: 990,
      description: "Solo founders running their own product or service.",
      bestFor: "Solo founders",
      features: [
        "1 brand",
        "Unlimited campaigns",
        "Unlimited reports",
        "Works in Claude, ChatGPT & Perplexity",
        "Budget-aware structuring on every tier",
        "Geo targeting (city, region, radius)",
      ],
      cta: "Start 7-day free trial",
      highlighted: false
    },
    {
      name: "Agency",
      monthlyPrice: 249,
      annualPrice: 2490,
      description: "Freelancers and consultants managing client work.",
      bestFor: "Freelancers & consultants",
      features: [
        "5 brands",
        "Unlimited campaigns",
        "Unlimited reports",
        "Multi-account / MCC support",
        "Per-brand value-prop docs",
        "All Solo features",
      ],
      cta: "Start 7-day free trial",
      highlighted: true
    },
    {
      name: "Studio",
      monthlyPrice: 599,
      annualPrice: 5990,
      description: "Small agencies running 10–25 active clients.",
      bestFor: "Small agencies",
      features: [
        "25 brands",
        "Unlimited campaigns",
        "Unlimited reports",
        "Priority email support from the founder",
        "All Agency features",
      ],
      cta: "Start 7-day free trial",
      highlighted: false
    }
  ];

  useEffect(() => {
    trackEvent('view_pricing_page');
  }, []);

  const handleSubscribeClick = (tierName: string) => {
    trackEvent('pricing_subscribe_click', tierName);
    
    if (tierName === "Enterprise") {
      window.open("mailto:george@upfixe.com?subject=Enterprise Plan Inquiry", "_blank");
    } else {
      window.location.href = "https://app.upfixe.com";
    }
  };

  const handleContactSales = () => {
    trackEvent('pricing_contact_sales_click');
    window.open("mailto:george@upfixe.com?subject=Enterprise Plan Inquiry", "_blank");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Pricing — Upfixe, the chat-native Google Ads platform</title>
        <meta name="description" content="Solo $99, Agency $249, Studio $599. 7-day free trial on every tier. First 50 customers get founder pricing — $49/month locked for life." />
        <meta name="keywords" content="Google Ads pricing, chat-native google ads, MCP server pricing, PPC agency alternative" />
        <meta property="og:title" content="Pricing — Upfixe, the chat-native Google Ads platform" />
        <meta property="og:description" content="Three tiers, monthly billing, 7-day free trial. Founder pricing $49/month locked for the first 50 customers." />
        <link rel="canonical" href="https://upfixe.com/pricing" />
      </Helmet>
      <Header />
      
      <main className="flex-grow">
        <PricingHero />

        <section className="px-4 pt-8" aria-labelledby="founder-pricing">
          <div className="container mx-auto max-w-7xl">
            <div className="rounded-xl border-2 border-primary bg-gradient-to-br from-primary/5 to-background p-6 md:p-8">
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Badge className="bg-primary text-primary-foreground hover:bg-primary">
                      <Sparkles className="h-3.5 w-3.5 mr-1" />
                      Founder pricing
                    </Badge>
                    <span className="text-sm font-medium text-muted-foreground">
                      First 50 customers only
                    </span>
                  </div>
                  <h2
                    id="founder-pricing"
                    className="text-2xl md:text-3xl font-bold text-foreground mb-2"
                  >
                    $49/month — <span className="text-primary">locked for life</span>
                  </h2>
                  <p className="text-muted-foreground">
                    Early access pricing for the first 50 customers. Half off
                    the Solo tier, every renewal, forever. After 50 sign-ups
                    this offer goes away and pricing returns to standard.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                  <TrialButton source="pricing_page_banner" tier="founder">
                    Claim founder pricing
                  </TrialButton>
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/mcp" onClick={() => trackEvent("pricing_mcp_learn_more_click")}>
                      How it works
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          className="py-20 px-4" 
          id="pricing"
          ref={pricingRef}
        >
          <div 
            className="container mx-auto max-w-7xl"
            style={{
              opacity: isInView ? 1 : 0,
              transform: isInView ? 'none' : 'translateY(20px)',
              transition: 'all 0.7s cubic-bezier(0.17, 0.55, 0.55, 1) 0.2s'
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {tiers.map((tier) => (
                <PricingTier
                  key={tier.name}
                  name={tier.name}
                  monthlyPrice={tier.monthlyPrice}
                  annualPrice={tier.annualPrice}
                  description={tier.description}
                  bestFor={tier.bestFor}
                  features={tier.features}
                  cta={tier.cta}
                  highlighted={tier.highlighted}
                  onSubscribe={handleSubscribeClick}
                />
              ))}
            </div>
            
            <EnterpriseTier onContactSales={handleContactSales} />
          </div>
        </section>

        <PricingFAQ />

        {/* Blog Links Section */}
        <section className="py-20 px-4 bg-blue-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Learn More About Optimization</h2>
              <p className="text-lg text-muted-foreground">
                Read our guides on reducing CPA, optimizing budgets, and scaling Google Ads efficiently.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a href="/blog/reduce-google-ads-cpa" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Reduce Google Ads CPA</h3>
                <p className="text-sm text-muted-foreground mb-4">7 proven strategies to cut your cost-per-acquisition in half.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article â†’</span>
              </a>
              <a href="/blog/budget-optimization-guide" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Budget Optimization Guide</h3>
                <p className="text-sm text-muted-foreground mb-4">Complete framework for maximizing ROI on every dollar spent.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article â†’</span>
              </a>
              <a href="/blog/ai-vs-manual-google-ads" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">AI vs Manual Ads</h3>
                <p className="text-sm text-muted-foreground mb-4">Compare costs and results: agencies vs AI platforms vs DIY.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article â†’</span>
              </a>
            </div>
          </div>
        </section>

        <PricingCTA />
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;